<template>
  <tr :class="rowClasses" @click="onRowClick">
    <td
        class="tree-cell"
        :style="{'padding-left': (depth * 32 + 12) + 'px', 'min-width': '320px'}"
    >
      <span
          v-if="isGroup && hasChildren"
          class="toggle-icon"
          :class="{ 'is-expanded': expanded }"
          @click.stop="toggleExpanded"
          aria-label="Toggle group"
      ></span>
      <span v-else class="toggle-spacer"></span>
      <span class="node-name">{{ node.name }}</span>
    </td>
    <td>{{ linkedItemDisplay }}</td>
    <td class="text-center">{{ unitDisplay }}</td>
    <td class="text-right">{{ commaFormat(stats.quantity) }}</td>
    <td class="text-right">{{ rateDisplay }}</td>
    <td class="text-right">{{ commaFormat(stats.budgetTotal) }}</td>
    <td class="text-right">0.00</td>
    <td class="text-right">0.00</td>
    <td class="text-right">{{ commaFormat(stats.requisitionQty) }}</td>
    <td class="text-right">{{ commaFormat(stats.poQty) }}</td>
    <td class="text-right">{{ commaFormat(stats.remainingQtyOrder) }}</td>
    <td class="text-right">{{ commaFormat(stats.poAmount) }}</td>
    <td class="text-right">{{ commaFormat(stats.remainingAmount) }}</td>
    <td class="text-right">{{ commaFormat(stats.stockInHand) }}</td>
    <td class="text-right">{{ commaFormat(stats.netQtyReceived) }}</td>
    <td class="text-right">{{ commaFormat(stats.remainingBoqQtyStock) }}</td>
  </tr>

  <tr v-if="isGroup && expanded && loadingChildren" class="hint-row">
    <td :style="{'padding-left': ((depth + 1) * 32 + 12) + 'px'}" colspan="16">
      <span class="spinner-border spinner-border-sm me-1"></span>
      <small class="text-muted">Loading children…</small>
    </td>
  </tr>

  <tr v-if="isGroup && expanded && !loadingChildren && loadedChildren.length === 0" class="hint-row">
    <td :style="{'padding-left': ((depth + 1) * 32 + 12) + 'px'}" colspan="16">
      <small class="text-muted">No items under this group.</small>
    </td>
  </tr>

  <template v-if="isGroup && expanded && loadedChildren.length > 0">
    <ReportTreeBrowser
        v-for="child in loadedChildren"
        :key="child.id"
        :node="child"
        :depth="depth + 1"
        :project-id="projectId"
        :budget-master-id="budgetMasterId"
        :cost-centre-id="costCentreId"
        :company-id="companyId"
        :offset="offset"
    />

    <tr v-if="childPagination.last_page > 1" class="pager-row">
      <td :style="{'padding-left': ((depth + 1) * 32 + 12) + 'px'}" colspan="16">
        <div class="d-flex align-items-center flex-wrap pager-inner">
          <small class="text-muted me-3">
            Page <strong>{{ childPagination.current_page }}</strong> of {{ childPagination.last_page }}
            · {{ childPagination.total }} items
          </small>
          <ul class="pagination pagination-sm mb-0">
            <li class="page-item" :class="{ disabled: childPagination.current_page <= 1 || loadingChildren }">
              <button class="page-link" @click="goToPage(childPagination.current_page - 1)">‹</button>
            </li>
            <li
                v-for="p in pageList"
                :key="p.key"
                class="page-item"
                :class="{ active: p.page === childPagination.current_page, disabled: p.disabled || loadingChildren }"
            >
              <button class="page-link" :disabled="p.disabled" @click="goToPage(p.page)">{{ p.label }}</button>
            </li>
            <li class="page-item" :class="{ disabled: childPagination.current_page >= childPagination.last_page || loadingChildren }">
              <button class="page-link" @click="goToPage(childPagination.current_page + 1)">›</button>
            </li>
          </ul>
        </div>
      </td>
    </tr>
  </template>
</template>

<script>
export default {
  name: 'ReportTreeBrowser'
}
</script>

<script setup>
import { computed, ref, inject } from 'vue';
import figureFormatter from "@/services/utils/figureFormatter";
import handleBudgets from "@/services/modules/procurement/budget";

const props = defineProps({
  node: { type: Object, required: true },
  depth: { type: Number, default: 0 },
  projectId: { type: [String, Number], required: true },
  budgetMasterId: { type: [String, Number], default: null },
  costCentreId: { type: [String, Number], default: null },
  companyId: { type: [String, Number], required: true },
  offset: { type: Number, default: 20 }
});

const expanded = ref(false);
const loadedChildren = ref([]);
const loadingChildren = ref(false);
const childPagination = ref({ current_page: 0, last_page: 1, total: 0, per_page: props.offset });
const { commaFormat } = figureFormatter();
const { fetchBoqStatusNodes } = handleBudgets();
const showError = inject('showError');

const isGroup = computed(() => props.node.type === 'group');
const hasChildren = computed(() => !!props.node.has_children || (props.node.children_count || 0) > 0);

const rowClasses = computed(() => {
  const classes = [isGroup.value ? 'group-row' : 'endpoint-row', depthClass.value];
  if (isGroup.value && hasChildren.value) classes.push('is-clickable');
  return classes;
});

const onRowClick = () => {
  if (! isGroup.value) return;
  if (! hasChildren.value) return;
  toggleExpanded();
};

const depthClass = computed(() => {
  if (! isGroup.value) return '';
  const level = parseInt(props.node.group_level);
  if (! isNaN(level)) return `level-${level}`;
  return `level-${props.depth + 1}`;
});

function isBlankValue(v) {
  if (v === null || v === undefined) return true;
  if (v === '' || v === '0' || v === 0) return true;
  return false;
}

const linkedItemDisplay = computed(() => {
  if (isGroup.value) return '';
  if (isBlankValue(props.node.linked_item)) return '';
  return props.node.linked_item;
});

const unitDisplay = computed(() => {
  if (isGroup.value) return '';
  if (isBlankValue(props.node.unit)) return '';
  return props.node.unit;
});

const rateDisplay = computed(() => {
  if (isGroup.value) return '';
  return commaFormat(stats.value.rate);
});

const stats = computed(() => {
  if (isGroup.value) {
    const t = props.node.totals || {};
    return {
      quantity: num(t.quantity),
      rate: num(t.rate),
      budgetTotal: num(t.budget_total),
      requisitionQty: num(t.requisition_qty),
      poQty: num(t.po_qty),
      poAmount: num(t.po_amount),
      remainingAmount: num(t.remaining_amount),
      stockInHand: num(t.stock_in_hand),
      netQtyReceived: num(t.net_qty_received),
      remainingQtyOrder: num(t.remaining_qty_order),
      remainingBoqQtyStock: num(t.remaining_boq_qty_stock),
    };
  }
  return endpointStats(props.node);
});

function num(v) {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
}

function endpointStats(node) {
  const bg = Array.isArray(node.budget_general) ? node.budget_general : [];
  const quantity = bg.reduce((s, b) => s + num(b.quantity), 0);
  const rate = bg.reduce((s, b) => s + num(b.rate), 0);
  const budgetTotal = bg.reduce((s, b) => s + num(b.amount), 0);
  const requisitionQty = num(node.requisition_qty);
  const poQty = num(node.po_qty);
  const poAmount = num(node.po_amount);
  const stockInHand = num(node.total_stock_qty);
  const netQtyReceived = num(node.net_qty_received);
  return {
    quantity, rate, budgetTotal,
    requisitionQty, poQty, poAmount,
    remainingAmount: budgetTotal - poAmount,
    stockInHand, netQtyReceived,
    remainingQtyOrder: quantity - poQty,
    remainingBoqQtyStock: quantity - netQtyReceived,
  };
}

const toggleExpanded = async () => {
  expanded.value = ! expanded.value;
  if (! expanded.value) return;
  if (loadedChildren.value.length > 0) return;
  await loadChildren(1);
};

const loadChildren = async (page) => {
  if (loadingChildren.value) return;
  loadingChildren.value = true;
  try {
    const params = [
      `company_id=${props.companyId}`,
      `parent_id=${props.node.id}`,
      `budget_master_id=${props.budgetMasterId || ''}`,
      `cost_centre_id=${props.costCentreId || ''}`,
      `page=${page}`,
      `offset=${props.offset}`,
    ].join('&');
    const response = await fetchBoqStatusNodes(props.projectId, `?${params}`);
    if (! response.status) {
      showError(response.message || 'Failed to load children');
      return;
    }
    loadedChildren.value = response.data.data || [];
    childPagination.value = {
      current_page: response.data.current_page,
      last_page: response.data.last_page,
      total: response.data.total,
      per_page: response.data.per_page,
    };
  } catch (error) {
    const msg = (error.response && error.response.data && error.response.data.message) || error.message;
    showError(msg);
  } finally {
    loadingChildren.value = false;
  }
};

const goToPage = (page) => {
  if (page < 1 || page > childPagination.value.last_page) return;
  if (page === childPagination.value.current_page) return;
  loadChildren(page);
};

const pageList = computed(() => {
  const current = childPagination.value.current_page;
  const last = childPagination.value.last_page;
  const list = [];
  const push = (page, label, disabled) => list.push({ key: `${label}-${page}`, page, label, disabled: !! disabled });

  if (last <= 7) {
    for (let i = 1; i <= last; i++) push(i, String(i), false);
    return list;
  }

  push(1, '1', false);
  if (current > 4) push(0, '…', true);

  const start = Math.max(2, current - 1);
  const end = Math.min(last - 1, current + 1);
  for (let i = start; i <= end; i++) push(i, String(i), false);

  if (current < last - 3) push(0, '…', true);
  push(last, String(last), false);
  return list;
});

</script>

<style scoped>
.endpoint-row { background-color: #ffffff; }
.endpoint-row:hover { background-color: #f6f8fa; }

.group-row {
  transition: background-color 0.15s ease;
}
.group-row:hover { background-color: #f6f8fa; }

.group-row.level-1 .tree-cell {
  border-left: 3px solid #1e5fad;
}
.group-row.level-1 .node-name {
  font-weight: 700;
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.group-row.level-2 .tree-cell {
  border-left: 2px solid #6b8db5;
}
.group-row.level-2 .node-name {
  font-weight: 600;
  font-size: 0.9rem;
}

.group-row.level-3 .node-name,
.group-row.level-4 .node-name,
.group-row.level-5 .node-name,
.group-row.level-other .node-name {
  font-weight: 500;
  font-size: 0.88rem;
  font-style: italic;
}
.is-clickable { cursor: pointer; }

.tree-cell { position: relative; }

.toggle-icon {
  position: relative;
  display: inline-block;
  width: 16px;
  height: 16px;
  margin-right: 8px;
  vertical-align: middle;
  cursor: pointer;
  border-radius: 3px;
  transition: background-color 0.15s ease, transform 0.2s ease;
}
.toggle-icon:hover { background-color: rgba(0, 0, 0, 0.06); }
.toggle-icon::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 4px 0 4px 6px;
  border-color: transparent transparent transparent #5e5873;
  transform: translate(-50%, -50%);
  transition: transform 0.2s ease;
}
.toggle-icon.is-expanded::before {
  transform: translate(-50%, -50%) rotate(90deg);
}

.toggle-spacer {
  display: inline-block;
  width: 16px;
  margin-right: 8px;
  vertical-align: middle;
}

.node-name { vertical-align: middle; }

.badge-count {
  display: inline-block;
  margin-left: 8px;
  padding: 1px 7px;
  background: rgba(0, 0, 0, 0.08);
  color: #4b5563;
  border-radius: 10px;
  font-size: 0.72rem;
  font-weight: 500;
  vertical-align: middle;
}

.hint-row { background-color: #fafbfc; }

.pager-row {
  background-color: #fafbfc;
  border-top: 1px dashed #d6dde5;
}
.pager-inner { padding: 4px 0; }

.text-right { text-align: right; }
.text-center { text-align: center; }

.pagination .page-link {
  padding: 0.2rem 0.55rem;
  font-size: 0.8rem;
  cursor: pointer;
  border-color: #e5e9ef;
  color: #344054;
}
.pagination .page-item.active .page-link {
  background-color: #4b6cb7;
  border-color: #4b6cb7;
  color: #fff;
}
.pagination .page-item.disabled .page-link { cursor: not-allowed; opacity: 0.5; }
</style>
